/**
 * Import/export of big-screen dashboards as JSON, so a layout built by one operator
 * can be shared with another. Widgets like graphs and the scan-rate grid read
 * venue-specific gate data, so a dashboard only makes sense on the venue it was
 * built for — imports are therefore accepted only into the matching venue (see
 * {@link parseDashboardExport}).
 *
 * The on-disk shape is a small versioned envelope rather than a bare dashboard
 * array, so the importer can recognise the file, reject unrelated JSON, and evolve
 * the format later without misreading old files.
 */
import {
  BigScreenDashboard,
  DashboardWidget,
} from '../models/big-screen-dashboard.model';

/** Marker identifying an exported dashboards file, stored under `kind`. */
export const DASHBOARD_EXPORT_KIND = 'attendance-big-screen-dashboards';

/** Current export schema version; bumped if the envelope shape changes. */
export const DASHBOARD_EXPORT_VERSION = 1;

/**
 * The JSON envelope written on export and read on import. `venueKey`/`venueName`
 * record which venue the dashboards belong to so the importer can enforce that they
 * are only restored on the same venue.
 */
export interface DashboardExport {
  kind: typeof DASHBOARD_EXPORT_KIND;
  version: number;
  /** The venue the exported dashboards were built for. */
  venueKey: string;
  /** Human-readable venue name at export time, for a clearer mismatch message. */
  venueName?: string;
  /** ISO timestamp stamped by the caller (scripts can't call Date.now()). */
  exportedAt: string;
  dashboards: BigScreenDashboard[];
}

/** Raised when an import file is not a recognisable dashboards export. */
export class DashboardImportError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'DashboardImportError';
  }
}

/**
 * Raised when a file is a valid export but for a different venue than the one the
 * user is importing into. Carries both venues so the caller can explain the
 * mismatch. Extends {@link DashboardImportError} so a single catch handles both.
 */
export class VenueMismatchError extends DashboardImportError {
  constructor(
    readonly fileVenueName: string,
    readonly targetVenueName: string
  ) {
    super(
      `This file was exported for "${fileVenueName}", but you're importing into ` +
        `"${targetVenueName}". Dashboards use venue-specific gate data, so they can ` +
        `only be imported on the venue they were built for.`
    );
    this.name = 'VenueMismatchError';
  }
}

/**
 * Builds the export envelope for a set of dashboards. The caller supplies the
 * timestamp (scripts can't call `Date.now()`), and is expected to pass dashboards
 * that all belong to {@link venueKey}.
 */
export function buildDashboardExport(
  dashboards: BigScreenDashboard[],
  venueKey: string,
  venueName: string,
  exportedAt: string
): DashboardExport {
  return {
    kind: DASHBOARD_EXPORT_KIND,
    version: DASHBOARD_EXPORT_VERSION,
    venueKey,
    venueName,
    exportedAt,
    dashboards,
  };
}

/** A single filename-safe token derived from arbitrary text (for downloads). */
export function slugify(text: string): string {
  return (
    text
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '') || 'dashboards'
  );
}

/**
 * Parses and validates raw import JSON, enforcing that it is a dashboards export
 * for {@link targetVenueKey}. Returns the dashboards as-is (the caller re-keys them
 * with fresh ids / the target venue). Throws {@link VenueMismatchError} when the
 * file is for another venue, or {@link DashboardImportError} for any malformed or
 * unrecognised input — never a raw `SyntaxError`, so callers surface one clear
 * message.
 */
export function parseDashboardExport(
  raw: string,
  targetVenueKey: string,
  targetVenueName: string
): BigScreenDashboard[] {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    throw new DashboardImportError('That file is not valid JSON.');
  }

  if (!isRecord(parsed) || parsed['kind'] !== DASHBOARD_EXPORT_KIND) {
    throw new DashboardImportError(
      'That file is not an exported dashboards file.'
    );
  }

  const version = parsed['version'];
  if (typeof version !== 'number' || version > DASHBOARD_EXPORT_VERSION) {
    throw new DashboardImportError(
      'That file was made by a newer version of the app and cannot be imported here.'
    );
  }

  const fileVenueKey = parsed['venueKey'];
  if (typeof fileVenueKey !== 'string' || fileVenueKey.length === 0) {
    throw new DashboardImportError('That file is missing its venue and cannot be imported.');
  }
  if (fileVenueKey !== targetVenueKey) {
    const fileVenueName =
      typeof parsed['venueName'] === 'string' && parsed['venueName']
        ? parsed['venueName']
        : fileVenueKey;
    throw new VenueMismatchError(fileVenueName, targetVenueName || targetVenueKey);
  }

  const dashboards = parsed['dashboards'];
  if (!Array.isArray(dashboards) || dashboards.length === 0) {
    throw new DashboardImportError('That file contains no dashboards to import.');
  }

  const valid = dashboards.filter(isValidDashboard);
  if (valid.length === 0) {
    throw new DashboardImportError('That file contains no readable dashboards.');
  }
  return valid;
}

/** Narrows unknown JSON to a plain object so property access is type-safe. */
function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * Minimal structural check that a parsed value is a dashboard: it must have a name
 * and a widget array. Widgets are trusted beyond being an array of objects, since
 * unknown per-type fields are ignored at render time and re-validated on save.
 */
function isValidDashboard(value: unknown): value is BigScreenDashboard {
  if (!isRecord(value)) return false;
  if (typeof value['name'] !== 'string' || value['name'].length === 0) return false;
  const widgets = value['widgets'];
  return Array.isArray(widgets) && widgets.every((widget) => isRecord(widget));
}

/**
 * Re-keys imported dashboards onto the target venue with fresh ids and timestamps,
 * so importing never collides with, or overwrites, the user's existing dashboards.
 * Each widget also gets a fresh id. The caller supplies the id factory and
 * timestamp to keep this pure and script-friendly.
 */
export function prepareImportedDashboards(
  dashboards: BigScreenDashboard[],
  targetVenueKey: string,
  createId: () => string,
  now: string
): BigScreenDashboard[] {
  return dashboards.map((dashboard) => ({
    ...dashboard,
    id: createId(),
    venueKey: targetVenueKey,
    // Venue-count fan-out is venue-specific too; drop any exported extra venues so
    // the import only surfaces the target venue's data.
    venueKeys: [],
    widgets: dashboard.widgets.map(reidWidget),
    createdAt: now,
    updatedAt: now,
  }));
}

/** Clones a widget with a fresh id, preserving all of its configuration. */
function reidWidget(widget: DashboardWidget): DashboardWidget {
  return {
    ...widget,
    id: crypto.randomUUID(),
    layout: { ...widget.layout },
  };
}
